import {Component, effect, inject, OnDestroy, signal} from '@angular/core';
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {MapContainerComponent} from "src/app/presentation/main-view/map-container/map-container-component";
import {NavButtonComponent} from "src/app/presentation/main-view/nav-bar/nav-button/nav-button.component";
import {NavBarComponent} from "src/app/presentation/main-view/nav-bar/nav-bar.component";
import {SearchBarComponent} from "src/app/presentation/common/search-bar/search-bar.component";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {Subject} from "rxjs";
import {OfflineComponent} from 'src/app/presentation/main-view/map-container/offline/offline.component';
import {NavBarStateStore} from 'src/app/presentation/main-view/nav-bar/nav-bar-state/nav-bar-state.store';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {
  InternalNotificationsComponent
} from "src/app/presentation/main-view/internal-notifications/internal-notifications.component";
import {FathomService} from 'src/app/analytics/fathom.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AssetsStore} from 'src/app/domain/assets/assets.store';

@Component({
  selector: 'app-main-view',
  imports: [
    MapContainerComponent,
    NavButtonComponent,
    NavBarComponent,
    OfflineComponent,
    SearchBarComponent,
    RouterOutlet,
    InternalNotificationsComponent,
],
  templateUrl: './main-view.component.html',
  styleUrl: './main-view.component.css'
})
export class MainViewComponent implements OnDestroy {

  disabled = false;
  loading = signal(true);
  filteredAssets: any[] = [];

  path = signal<string | undefined>(undefined);

  informationCenterVisible = signal(false);
  mapVisible = signal(false);
  notificationsVisible = signal(false);

  mainViewStore = inject(MainViewStore);

  private assetsStore = inject(AssetsStore);
  private menuStore = inject(NavBarStateStore);
  private router = inject(Router);

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private dataSubscriptionService: DataSubscriptionService,
    private fathomService: FathomService,
    protected viewportService: ViewportService,
  ) {
    this.viewportService.checkViewportSize();

    effect(() => {
      this.disabled = this.loading() || !this.menuStore.enabled();
    });

    effect(() => {
      this.loading.set(this.assetsStore.loading());
    });

    this.path.set(this.router.url);
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.path.set(event.urlAfterRedirects);
      }
    });

    effect(() => {
      const childComponentVisible = this.mainViewStore.childComponentVisible();
      const path = this.path();
      this.informationCenterVisible.set(childComponentVisible && path === '/main/information-center');
      this.mapVisible.set(!childComponentVisible);
      this.notificationsVisible.set(childComponentVisible && path === '/main/notifications');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  toggleInformationCenter() {
    const informationCenterVisible = this.informationCenterVisible();
    if (informationCenterVisible) {
      this.mainViewStore.hideChildComponent();
    } else {
      this.navigate('/main/information-center');
    }
  }

  toggleMap() {
    const mapVisible = this.mapVisible();
    this.fathomService.trackEvent('Toggle full-screen map');
    if (mapVisible) {
      this.mainViewStore.showChildComponent();
    } else {
      this.mainViewStore.hideChildComponent();
    }
  }

  toggleNotifications() {
    const notificationsVisible = this.notificationsVisible();
    if (notificationsVisible) {
      this.mainViewStore.hideChildComponent();
    } else {
      this.navigate('/main/notifications');
    }
  }

  private navigate(url: string) {
    if (this.disabled) return;

    this.mainViewStore.showChildComponent();
    this.router.navigate([url]);
  }
}
